import { Component, OnDestroy, OnInit } from '@angular/core';

import { Subject, filter, takeUntil } from 'rxjs';

import { TranslationService } from '@mirage-projects/cw-commons';
import { DistanceUnit, GisJobAdapter, ImporterConverterOptions } from '@mirage-projects/cw-models';
import { DbPcvizService, JobPcvizService } from '@mirage-projects/cw-pcviz-core';

import { UploadWizardService } from '../../../services/upload-wizard-service/upload-wizard.service';
import { GisImporterConversionOptions } from '../../create-wizard/gis-import-options/gis-import-options.component';

/**
 * Component to add the new layer options
 */
@Component({
    selector: 'cw-add-layer-options',
    templateUrl: './add-layer-options.component.html',
    styleUrls: ['add-layer-options.component.scss'],
    standalone: false,
})
export class AddLayerOptionsComponent implements OnInit, OnDestroy {
    gisConversion!: GisImporterConversionOptions;

    readonly defaultMaxLength = 255;
    private onDestroy$ = new Subject<void>();
    private wizardTabName = 'WIZARD.TAB_LAYER_OPTIONS';

    constructor(
        public uploadWizardService: UploadWizardService,
        private dbPcvizService: DbPcvizService,
        private jobAdapterService: JobPcvizService,
        private translationService: TranslationService
    ) {}

    ngOnInit(): void {
        this.uploadWizardService.layerSelectionMode = 'new';
        this.gisConversion = this.uploadWizardService.gisConversion;
        this.uploadWizardService.stepChanged
            .pipe(
                takeUntil(this.onDestroy$),
                filter((wizardComponent) => {
                    const step = this.translationService.translate(this.wizardTabName);
                    return this.uploadWizardService.comparePreviousStep(wizardComponent, step);
                })
            )
            .subscribe(() => {
                this.updateSummary();
            });
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    updateSummary(): void {
        let fields = [{ key: 'CREATE_PROJECT.LAYER_NAME', value: this.uploadWizardService.layerForm.controls['name'].value ?? '' }];

        if (this.isCsvGeometry) {
            fields = [...fields, ...this.getGcpFields()];
            this.setImporterOptions();
        }

        this.uploadWizardService.summary[1] = {
            title: 'WIZARD.TAB_LAYER_OPTIONS',
            fields,
        };
    }

    get isCsvGeometry(): boolean {
        return this.jobAdapterService.getJobAdapter()?.getDetectedType() === 'Gis:csv';
    }

    get csvTypes(): string[] {
        return this.isCsvGeometry ? Object.keys((this.jobAdapterService.getJobAdapter() as GisJobAdapter).getCsvOptions()) : [];
    }

    get scaleFactorDefinitions(): DistanceUnit[] {
        return this.dbPcvizService.getDistanceUnits();
    }

    private getGcpFields(): Array<{ key: string; value: string }> {
        return [
            { key: 'UPLOAD.CSV_GIS_IMPORTER_SELECT_TYPE', value: this.gisConversion.csvType ?? '' },
            { key: 'UPLOAD.CSV_GIS_IMPORTER_SELECT_SCALE_FACTOR', value: this.getScaleFactorLabel() },
        ];
    }

    private getScaleFactorLabel(): string {
        const scaleFactor = this.scaleFactorDefinitions.find((item) => item.fromMeters === this.gisConversion.ScaleFactor);

        return scaleFactor?.label || '';
    }

    private setImporterOptions(): void {
        const importerOptions: ImporterConverterOptions = {
            ScaleFactor: this.gisConversion.ScaleFactor,
            CSVHeaders: (this.jobAdapterService.getJobAdapter() as GisJobAdapter).getCsvOptions()[this.gisConversion.csvType],
        };
        this.jobAdapterService.getJobAdapter()?.setImporterOptions(importerOptions);
    }
}
